<template>
  <div class="p-2 rounded-xl min-h-screen">
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-4 shadow-sm border border-gray-100">
      <div class="flex items-center justify-between">
        <div class="text-sm font-extrabold text-gray-800">ادغام محصولات تکراری</div>
        <button
            class="text-[11px] px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold"
            :disabled="loading"
            @click="fetchDuplicates"
        >
          بروزرسانی
        </button>
      </div>
      <div class="text-[11px] text-gray-500 mt-2 leading-5">
        گروه‌هایی که کد ۱۶ رقمی (بارکد) مشترک دارند نمایش داده می‌شوند. قبل از ادغام حتماً تصویر و نام هر مورد را
        بررسی کنید — بعضی گروه‌ها ممکن است محصولات واقعاً متفاوتی باشند (مثلاً رنگ/طعم متفاوت) که فقط بارکد مشترک
        دارند؛ در این صورت با تیک برداشتن، آن مورد را از ادغام کنار بگذارید.
      </div>
      <div class="mt-3 flex gap-2">
        <input
            v-model="search"
            placeholder="جستجو با GTIN، نام محصول یا شناسه محصول"
            class="flex-1 px-3 py-2 bg-gray-100 text-xs rounded-lg border text-right"
            @keyup.enter="applySearch"
        >
        <button
            class="text-[11px] px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold flex-shrink-0"
            :disabled="loading"
            @click="applySearch"
        >
          جستجو
        </button>
        <button
            v-if="search"
            class="text-[11px] px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold flex-shrink-0"
            @click="search = ''; applySearch()"
        >
          پاک کردن
        </button>
      </div>
      <label class="mt-3 flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer w-fit">
        <input type="checkbox" v-model="hasInventory" @change="applySearch">
        فقط گروه‌هایی که حداقل یکی از اعضایشان موجودی دارد
      </label>
    </div>

    <div v-if="loading" class="text-center text-xs text-gray-400 py-10">در حال بارگذاری...</div>
    <div v-else-if="!groups.length" class="text-center text-xs text-gray-400 py-10">
      هیچ گروه محصول تکراری‌ای یافت نشد.
    </div>

    <div v-else class="space-y-4">
      <div
          v-for="group in groups"
          :key="group.match_key"
          class="bg-white rounded-xl border border-gray-100 shadow-sm p-3"
      >
        <div class="flex items-center justify-between mb-2">
          <div class="text-xs font-bold text-gray-700">
            کد: {{ group.match_key }}
            <span class="text-gray-400 font-normal">({{ group.products.length }} محصول)</span>
            <span
                v-if="group.likely_variants"
                class="mr-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold align-middle"
                title="ممکن است این محصولات تنوع رنگ/طعم/شماره داشته باشند، نه یک محصول تکراری واقعی — قبل از ادغام حتماً بررسی کنید"
            >
              متغیر دار
            </span>
          </div>
        </div>

        <div class="space-y-1.5">
          <div
              v-for="p in group.products"
              :key="p.id"
              class="flex items-center gap-2 p-2 rounded-lg border"
              :class="selectedMain[group.match_key] === p.id
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-gray-50 border-gray-100'"
          >
            <input
                type="radio"
                :name="'main-' + group.match_key"
                :value="p.id"
                v-model="selectedMain[group.match_key]"
                @change="onMainChange(group)"
            >
            <img :src="img_source(p.image)" class="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0">
            <div class="flex-1 min-w-0">
              <div class="text-[12px] font-bold text-gray-800 truncate">{{ p.name }}</div>
              <div class="text-[10px] text-gray-400">
                #{{ p.id }} · {{ fmtNum(p.price) }} تومان · موجودی {{ fmtNum(p.inventory_count) }} · {{ statusLabel(p.status) }}
                <span
                    class="px-1.5 py-0.5 rounded font-bold"
                    :class="p.product_type === 'variation' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'"
                >
                  {{ p.product_type === 'variation' ? 'متغیر' : 'ساده' }}
                </span>
                <span v-if="!p.active" class="text-rose-500 font-bold">(غیرفعال)</span>
              </div>
              <div v-if="p.variation_of" class="text-[10px] text-indigo-500">
                زیرمجموعهٔ: {{ p.variation_of.name }} (#{{ p.variation_of.id }})
              </div>
              <div class="text-[10px] text-gray-400">
                شریک: {{ p.partner ? p.partner.name : 'بدون شریک' }}
              </div>
            </div>
            <button
                class="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex-shrink-0"
                title="خلاصهٔ محصول"
                @click="openQuickView(p)"
            >
              <v-icon size="16">fa-eye</v-icon>
            </button>
            <span v-if="selectedMain[group.match_key] === p.id" class="text-[10px] font-bold text-emerald-600 flex-shrink-0">
              محصول اصلی
            </span>
            <label v-else class="flex items-center gap-1 text-[10px] text-gray-500 flex-shrink-0 cursor-pointer">
              <input type="checkbox" :checked="isIncluded(group, p.id)" @change="toggleIncluded(group, p.id)">
              ادغام شود
            </label>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-3">
          <button
              class="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-bold hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="!includedCount(group)"
              @click="previewMerge(group)"
          >
            بررسی ادغام ({{ includedCount(group) }} مورد)
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="previewDialog.open" class="rounded-xl" max-width="640" content-class="dark-v-dialog">
      <v-card class="rounded-2xl overflow-hidden">
        <div class="px-4 py-3 bg-gray-900/70 border-b border-white/10 flex items-center justify-between">
          <div class="text-sm font-extrabold text-white">
            {{ isResultMode ? 'نتیجهٔ ادغام' : 'پیش‌نمایش ادغام' }}
          </div>
          <button class="p-2 rounded-lg hover:bg-white/10" @click="previewDialog.open = false">
            <v-icon color="white">fa-times</v-icon>
          </button>
        </div>

        <div class="p-4 space-y-3 max-h-[65vh] overflow-y-auto text-slate-200">
          <div v-if="previewDialog.busy" class="text-center text-xs text-slate-400 py-8">در حال بررسی...</div>
          <div v-else-if="applyBusy" class="text-center text-xs text-slate-400 py-8">در حال اعمال ادغام...</div>

          <template v-else>
            <div
                v-if="previewDialog.finalReport"
                class="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-[12px] font-bold text-emerald-300"
            >
              ✓ ادغام با موفقیت انجام شد
            </div>
            <div
                v-else-if="previewDialog.finalError"
                class="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 text-[12px] font-bold text-rose-300"
            >
              ✗ ادغام ناموفق بود: {{ previewDialog.finalError }}
            </div>

            <template v-if="activeReport">
              <div
                  v-if="activeReport.warnings && activeReport.warnings.length"
                  class="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-1"
              >
                <div v-for="(w, i) in activeReport.warnings" :key="i" class="text-[11px] text-amber-300">
                  {{ w }}
                </div>
              </div>

              <div class="bg-white/5 rounded-xl border border-white/10 p-3 text-[11px] space-y-1">
                <div>محصول اصلی: <span class="font-bold text-white">#{{ activeReport.main_id }}</span></div>
                <div>محصولات ادغام‌شونده: <span class="font-bold text-white">{{ activeReport.duplicate_ids.join('، ') }}</span></div>
              </div>

              <div class="bg-white/5 rounded-xl border border-white/10 p-3 text-[11px]">
                <div class="font-bold text-slate-300 mb-1">وضعیت انتشار محصول اصلی</div>
                <select
                    v-if="!isResultMode"
                    v-model="mainStatus"
                    class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                >
                  <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <div v-if="!isResultMode" class="text-[10px] text-slate-400 mt-1">
                  در حالت عادی همین وضعیت فعلی محصول اصلی باقی می‌ماند؛ فقط اگر آن را عوض کنید تغییر می‌کند.
                </div>
                <div v-else class="text-[11px] text-slate-300">
                  <span v-if="activeReport.changes.status">
                    از «{{ statusLabel(activeReport.changes.status.from) }}» به «{{ statusLabel(activeReport.changes.status.to) }}» تغییر کرد.
                  </span>
                  <span v-else>بدون تغییر (همان وضعیت قبلی باقی ماند).</span>
                </div>
              </div>

              <div class="bg-white/5 rounded-xl border border-white/10 p-3 text-[11px]">
                <div class="font-bold text-slate-300 mb-1">شریک محصول اصلی</div>
                <select
                    v-if="!isResultMode"
                    v-model="mainPartnerId"
                    class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                >
                  <option :value="null">بدون شریک</option>
                  <option v-for="pt in partners" :key="pt.id" :value="pt.id">{{ pt.name }}</option>
                </select>
                <div v-if="!isResultMode" class="text-[10px] text-slate-400 mt-1">
                  در حالت عادی همین شریک فعلی محصول اصلی باقی می‌ماند؛ فقط اگر آن را عوض کنید تغییر می‌کند.
                </div>
                <div v-else class="text-[11px] text-slate-300">
                  <span v-if="activeReport.changes.partner">
                    از «{{ partnerName(activeReport.changes.partner.from) }}» به «{{ partnerName(activeReport.changes.partner.to) }}» تغییر کرد.
                  </span>
                  <span v-else>بدون تغییر (همان شریک قبلی باقی ماند).</span>
                </div>
              </div>

              <div class="bg-white/5 rounded-xl border border-white/10 p-3 text-[11px]">
                <div class="font-bold text-slate-300 mb-2">قیمت‌گذاری محصول اصلی</div>
                <template v-if="!isResultMode">
                  <div class="mb-3">
                    <div class="text-[10px] text-slate-400 mb-1">
                      سورس قیمت (جدیدترین بالا) — با انتخاب هرکدام، همهٔ فیلدهای قیمت زیر از همان محصول پر می‌شود
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <button
                          v-for="p in pricingSourceCandidates"
                          :key="p.id"
                          type="button"
                          class="px-2 py-1 rounded-lg text-[10px] border"
                          :class="mainPricingSourceId === p.id
                            ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200'
                            : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'"
                          @click="usePricingSource(p)"
                      >
                        #{{ p.id }} · {{ fmtNum(p.base_price) }} {{ currencyName(p.currency) }} ·
                        سود {{ fmtNum(p.profit_margin) }} ({{ profitTypeLabel(p.profit_type) }})
                      </button>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <label class="text-[10px] text-slate-400 block mb-1">نوع ارز</label>
                      <select
                          v-model="mainPricing.currency_id"
                          class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                      >
                        <option :value="null">—</option>
                        <option v-for="c in currencies" :key="c.id" :value="c.id">{{ c.name }}</option>
                      </select>
                    </div>
                    <div>
                      <label class="text-[10px] text-slate-400 block mb-1">قیمت ارزی</label>
                      <input
                          v-model.number="mainPricing.base_price"
                          type="number"
                          class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                      >
                    </div>
                    <div>
                      <label class="text-[10px] text-slate-400 block mb-1">مقدار سود</label>
                      <input
                          v-model.number="mainPricing.profit_margin"
                          type="number"
                          class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                      >
                    </div>
                    <div>
                      <label class="text-[10px] text-slate-400 block mb-1">مقدار تخفیف</label>
                      <input
                          v-model.number="mainPricing.discount_margin"
                          type="number"
                          class="w-full px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-white"
                      >
                    </div>
                  </div>
                  <div class="mt-2 pt-2 border-t border-white/10 text-[11px] text-slate-300">
                    قیمت فروش تومانی: <span class="font-bold text-white">{{ fmtNum(tomanSellingPrice) }} تومان</span>
                  </div>
                  <div v-if="mainPricing.currency_id === TOMAN_ID" class="text-[11px] text-slate-300">
                    قیمت مصرف‌کننده: <span class="font-bold text-white">{{ fmtNum(mainRegularPrice) }} تومان</span>
                  </div>
                  <div class="text-[10px] text-slate-400 mt-2">
                    در حالت عادی همین مقادیر فعلی محصول اصلی باقی می‌مانند؛ فقط اگر تغییرشان دهید اعمال می‌شوند.
                  </div>
                </template>
                <template v-else>
                  <div v-if="pricingOverrideRows(activeReport).length" class="space-y-1">
                    <div
                        v-for="row in pricingOverrideRows(activeReport)"
                        :key="row.field"
                        class="text-[11px] text-slate-300"
                    >
                      {{ row.label }}: از «{{ row.from }}» به «{{ row.to }}» تغییر کرد.
                    </div>
                  </div>
                  <div v-else class="text-[11px] text-slate-300">بدون تغییر (همان مقادیر قبلی باقی ماندند).</div>
                </template>
              </div>

              <div v-if="storeInventoryTotal(activeReport)" class="bg-white/5 rounded-xl border border-white/10 p-3">
                <div class="text-[11px] font-bold text-slate-300 mb-2">
                  {{ isResultMode ? 'انتقال موجودی انبار' : 'انتقال موجودی انبار — مواردی را که نمی‌خواهید تیک بردارید' }}
                </div>
                <label
                    v-for="mv in activeReport.changes.store_inventory_moved"
                    :key="mv.key"
                    class="flex items-start gap-2 py-1.5 border-t border-white/10 first:border-t-0"
                    :class="isResultMode ? '' : 'cursor-pointer'"
                >
                  <input
                      v-if="!isResultMode"
                      type="checkbox"
                      class="mt-0.5"
                      :checked="!isExcluded('storeInventory', mv.key)"
                      @change="toggleField('storeInventory', mv.key)"
                  >
                  <span v-else class="mt-0.5 text-[12px]" :class="mv.applied ? 'text-emerald-400' : 'text-slate-500'">
                    {{ mv.applied ? '✓' : '✗' }}
                  </span>
                  <span class="text-[11px] text-slate-300 flex-1">
                    <span class="font-bold text-white">{{ mv.store_name || ('انبار #' + mv.store_id) }}</span>:
                    {{ fmtNum(mv.inventory) }} عدد از محصول #{{ mv.from_product }}
                    <div class="text-[10px] text-slate-400">موجودی جدید بعد از ادغام: {{ fmtNum(mv.new_total) }}</div>
                  </span>
                </label>
              </div>

              <div
                  v-if="activeReport.changes.total_inventory_moved && activeReport.changes.total_inventory_moved.length"
                  class="bg-white/5 rounded-xl border border-white/10 p-3"
              >
                <div class="text-[11px] font-bold text-slate-300 mb-1">انتقال موجودی کل</div>
                <div
                    v-for="(mv, i) in activeReport.changes.total_inventory_moved"
                    :key="'ti-' + i"
                    class="text-[11px] text-slate-300"
                >
                  {{ fmtNum(mv.inventory) }} عدد از محصول #{{ mv.from_product }}
                </div>
              </div>

              <div v-if="redirectEntries(activeReport).length" class="bg-white/5 rounded-xl border border-white/10 p-3">
                <div class="text-[11px] font-bold text-slate-300 mb-1">رفرنس‌هایی که به محصول اصلی منتقل می‌شوند</div>
                <div class="grid grid-cols-2 gap-x-3 gap-y-1">
                  <div
                      v-for="([label, count], i) in redirectEntries(activeReport)"
                      :key="'r-' + i"
                      class="text-[11px] text-slate-300 flex justify-between"
                  >
                    <span class="truncate">{{ label }}</span>
                    <span class="font-bold text-white flex-shrink-0">{{ fmtNum(count) }}</span>
                  </div>
                </div>
              </div>

              <div v-if="redirectConflictEntries(activeReport).length" class="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                <div class="text-[11px] font-bold text-amber-300 mb-1">
                  موارد تکراری که به‌جای انتقال، حذف می‌شوند (چون محصول اصلی از قبل همان رابطه را دارد)
                </div>
                <div class="grid grid-cols-2 gap-x-3 gap-y-1">
                  <div
                      v-for="([label, count], i) in redirectConflictEntries(activeReport)"
                      :key="'rc-' + i"
                      class="text-[11px] text-amber-200 flex justify-between"
                  >
                    <span class="truncate">{{ label }}</span>
                    <span class="font-bold text-amber-100 flex-shrink-0">{{ fmtNum(count) }}</span>
                  </div>
                </div>
              </div>

              <div
                  v-if="activeReport.changes.orphaned_variable_parents_deactivated && activeReport.changes.orphaned_variable_parents_deactivated.length"
                  class="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3"
              >
                <div class="text-[11px] font-bold text-amber-300 mb-1">
                  {{ isResultMode ? 'محصولات والدِ بدون‌متغیر که غیرفعال شدند' : 'این محصولات والد پس از ادغام دیگر متغیر فعالی نخواهند داشت و خودکار غیرفعال می‌شوند' }}
                </div>
                <div
                    v-for="parent in activeReport.changes.orphaned_variable_parents_deactivated"
                    :key="parent.id"
                    class="text-[11px] text-amber-200"
                >
                  {{ parent.name }} (#{{ parent.id }})
                </div>
              </div>

              <div v-if="fieldRows(activeReport).length" class="bg-white/5 rounded-xl border border-white/10 p-3">
                <div class="text-[11px] font-bold text-slate-300 mb-2">
                  {{ isResultMode
                    ? 'فیلدهایی که از سایر محصولات گروه در محصول اصلی اعمال شد'
                    : 'فیلدهایی که از سایر محصولات گروه پیشنهاد می‌شود در محصول اصلی جایگزین شود — هرکدام را که نمی‌خواهید تیک بردارید' }}
                </div>
                <label
                    v-for="row in fieldRows(activeReport)"
                    :key="row.category + '-' + row.field"
                    class="flex items-start gap-2 py-1.5 border-t border-white/10 first:border-t-0"
                    :class="isResultMode ? '' : 'cursor-pointer'"
                >
                  <input
                      v-if="!isResultMode"
                      type="checkbox"
                      class="mt-0.5"
                      :checked="!isExcluded(row.category, row.field)"
                      @change="toggleField(row.category, row.field)"
                  >
                  <span v-else class="mt-0.5 text-[12px]" :class="row.applied ? 'text-emerald-400' : 'text-slate-500'">
                    {{ row.applied ? '✓' : '✗' }}
                  </span>
                  <span class="text-[11px] text-slate-300 flex-1">
                    <span class="font-bold text-white">{{ row.label }}</span>
                    از محصول #{{ row.sourceProduct }}
                    <span v-if="row.detail"> ({{ row.detail }})</span>
                  </span>
                </label>
              </div>

              <div
                  v-if="activeReport.changes.gallery_moved && activeReport.changes.gallery_moved.length"
                  class="bg-white/5 rounded-xl border border-white/10 p-3"
              >
                <div class="text-[11px] font-bold text-slate-300 mb-2">
                  {{ isResultMode
                    ? 'تصاویر گالری که به محصول اصلی منتقل شد'
                    : 'تصاویر گالری که به محصول اصلی منتقل می‌شود — تصاویر ناخواسته را از تیک بردارید' }}
                </div>
                <div class="grid grid-cols-4 gap-2">
                  <label
                      v-for="img in activeReport.changes.gallery_moved"
                      :key="img.gallery_id"
                      class="relative"
                      :class="isResultMode ? '' : 'cursor-pointer'"
                  >
                    <img :src="img_source(img.image)" class="w-full aspect-square object-cover rounded-lg border border-white/10">
                    <input
                        v-if="!isResultMode"
                        type="checkbox"
                        class="absolute top-1 right-1"
                        :checked="!isExcluded('gallery', img.gallery_id)"
                        @change="toggleField('gallery', img.gallery_id)"
                    >
                    <span
                        v-else
                        class="absolute top-1 right-1 text-[12px] bg-black/60 rounded px-1"
                        :class="img.applied ? 'text-emerald-400' : 'text-rose-400'"
                    >
                      {{ img.applied ? '✓' : '✗' }}
                    </span>
                    <span class="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white text-center rounded-b-lg">
                      #{{ img.from_product }}
                    </span>
                  </label>
                </div>
              </div>
            </template>
          </template>
        </div>

        <div class="flex items-center justify-end gap-2 p-4 border-t border-white/10">
          <template v-if="isResultMode">
            <button
                class="px-4 py-2 rounded-xl text-[12px] font-bold bg-gradient-to-r from-emerald-500/60 to-teal-500/50 border border-white/10"
                @click="closeResultDialog"
            >
              بستن
            </button>
          </template>
          <template v-else>
            <button
                class="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-[12px]"
                @click="previewDialog.open = false"
            >
              انصراف
            </button>
            <button
                class="px-4 py-2 rounded-xl text-[12px] font-bold bg-gradient-to-r from-emerald-500/60 to-teal-500/50 border border-white/10 disabled:opacity-40"
                :disabled="applyBusy || previewDialog.busy || !previewDialog.report"
                @click="confirmApply"
            >
              تایید و ادغام نهایی
            </button>
          </template>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="quickView.open" class="rounded-xl" max-width="480" content-class="dark-v-dialog">
      <v-card class="rounded-2xl overflow-hidden">
        <div class="px-4 py-3 bg-gray-900/70 border-b border-white/10 flex items-center justify-between">
          <div class="text-sm font-extrabold text-white">خلاصهٔ محصول</div>
          <button class="p-2 rounded-lg hover:bg-white/10" @click="quickView.open = false">
            <v-icon color="white">fa-times</v-icon>
          </button>
        </div>
        <div class="p-4 space-y-3 max-h-[65vh] overflow-y-auto text-slate-200">
          <div v-if="quickView.busy" class="text-center text-xs text-slate-400 py-8">در حال بارگذاری...</div>
          <template v-else-if="quickView.data">
            <div class="flex items-center gap-3">
              <img :src="img_source(quickView.data.picture)" class="w-16 h-16 rounded-lg object-cover border border-white/10 flex-shrink-0">
              <div class="min-w-0">
                <div class="text-[13px] font-bold text-white truncate">{{ quickView.data.name }}</div>
                <div class="text-[10px] text-slate-400">#{{ quickView.data.id }} · {{ quickView.data.sixteen_digit_code }}</div>
              </div>
            </div>

            <div class="bg-white/5 rounded-xl border border-white/10 p-3 grid grid-cols-2 gap-y-1 text-[11px]">
              <div>قیمت فروش تومانی: <span class="font-bold text-white">{{ fmtNum(quickView.data.price) }}</span></div>
              <div v-if="quickView.data.currency === TOMAN_ID">
                قیمت مصرف‌کننده: <span class="font-bold text-white">{{ fmtNum(quickView.data.regular_price) }}</span>
              </div>
              <div>وضعیت: <span class="font-bold text-white">{{ statusLabel(quickView.data.status) }}</span></div>
              <div>موجودی کل: <span class="font-bold text-white">{{ fmtNum(quickView.data.calculate_current_inventory) }}</span></div>
            </div>

            <div
                v-if="quickView.data.inventories_in_stores && quickView.data.inventories_in_stores.length"
                class="bg-white/5 rounded-xl border border-white/10 p-3"
            >
              <div class="text-[11px] font-bold text-slate-300 mb-1">موجودی به تفکیک انبار</div>
              <div
                  v-for="inv in quickView.data.inventories_in_stores"
                  :key="inv.store"
                  class="text-[11px] text-slate-300 flex justify-between"
              >
                <span>{{ inv.name }}</span>
                <span class="font-bold text-white">{{ fmtNum(inv.inventory) }}</span>
              </div>
            </div>

            <div v-if="quickView.data.explanation" class="bg-white/5 rounded-xl border border-white/10 p-3 text-[11px] text-slate-300">
              {{ truncate(stripHtml(quickView.data.explanation), 300) }}
            </div>

            <div
                v-if="quickView.data.gallery && quickView.data.gallery.length"
                class="grid grid-cols-4 gap-2"
            >
              <img
                  v-for="g in quickView.data.gallery"
                  :key="g.id"
                  :src="img_source(g.picture)"
                  class="w-full aspect-square object-cover rounded-lg border border-white/10"
              >
            </div>
          </template>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'AdminProductMerge',
  data() {
    return {
      loading: false,
      search: '',
      hasInventory: false,
      groups: [],
      selectedMain: {},
      includedIds: {},
      TOMAN_ID: 19,
      mainStatus: null,
      currencies: [],
      partners: [],
      mainPartnerId: null,
      mainPricing: {
        currency_id: null, base_price: null, profit_margin: null, discount_margin: null,
      },
      mainShippingCost: 0,
      mainProfitType: 'percent',
      mainDiscountType: 'percent',
      mainRegularPrice: null,
      mainPricingSourceId: null,
      pricingSourceExplicit: false,
      pricingLabelMap: {
        currency_id: 'نوع ارز',
        base_price: 'قیمت ارزی',
        profit_margin: 'مقدار سود',
        discount_margin: 'مقدار تخفیف',
      },
      quickView: {
        open: false,
        busy: false,
        data: null,
      },
      statusLabelMap: {
        pr: 'نیاز به بررسی',
        pending: 'در انتظار تایید',
        draft: 'پیش‌نویس',
        publish: 'منتشر شده',
      },
      previewDialog: {
        open: false,
        group: null,
        report: null,
        busy: false,
        finalReport: null,
        finalError: null,
      },
      excluded: {
        content: new Set(),
        dimensions: new Set(),
        pricing: new Set(),
        gallery: new Set(),
        picture: new Set(),
        storeInventory: new Set(),
      },
      applyBusy: false,
      fieldLabels: {
        content: {
          explanation: 'توضیحات',
          summary_explanation: 'توضیح خلاصه',
          how_to_use: 'طریقه استفاده',
          faq: 'سوالات متداول',
          blog: 'بلاگ',
        },
        dimensions: {
          postal_weight: 'وزن پستی',
          length: 'طول',
          width: 'عرض',
          height: 'ارتفاع',
        },
        pricing: {
          shipping_cost: 'هزینه ارسال',
          currency_id: 'واحد پول',
          profit_type: 'نوع سود',
          profit_margin: 'درصد سود',
          discount_type: 'نوع تخفیف',
          discount_margin: 'درصد تخفیف',
          base_price: 'قیمت پایه',
        },
        picture: {
          main: 'تصویر اصلی محصول',
        },
      },
    };
  },
  computed: {
    statusOptions() {
      return Object.entries(this.statusLabelMap).map(([value, label]) => ({ value, label }));
    },
    activeReport() {
      return this.previewDialog.finalReport || this.previewDialog.report;
    },
    isResultMode() {
      return !!(this.previewDialog.finalReport || this.previewDialog.finalError);
    },
    pricingSourceCandidates() {
      const products = (this.previewDialog.group && this.previewDialog.group.products) || [];
      return products.slice().sort((a, b) => b.id - a.id);
    },
    currentCurrencyRate() {
      if (!this.mainPricing.currency_id) return 1;
      const c = this.currencies.find((cur) => cur.id === this.mainPricing.currency_id);
      return (c && Number(c.exchange_rate_to_toman)) || 1;
    },
    tomanSellingPrice() {
      const rate = this.currentCurrencyRate;
      const basePriceWithRate = (this.mainPricing.base_price || 0) * rate;
      const shippingCostWithRate = (this.mainShippingCost || 0) * rate;
      const profitAmount = this.mainProfitType === 'percent'
        ? ((basePriceWithRate + shippingCostWithRate) / 100) * (this.mainPricing.profit_margin || 0)
        : (this.mainPricing.profit_margin || 0);
      const priceWithProfit = profitAmount + basePriceWithRate + shippingCostWithRate;
      const discountAmount = this.mainDiscountType === 'percent'
        ? (priceWithProfit / 100) * (this.mainPricing.discount_margin || 0)
        : (this.mainPricing.discount_margin || 0);
      return priceWithProfit - discountAmount;
    },
  },
  mounted() {
    this.fetchDuplicates();
    this.fetchCurrencies();
    this.fetchPartners();
  },
  methods: {
    applySearch() {
      this.fetchDuplicates();
    },
    fetchCurrencies() {
      this.request({
        url: this.endpoint('main/currency'),
        method: 'get',
        success: (data) => {
          this.currencies = data || [];
        },
      });
    },
    fetchPartners() {
      this.request({
        url: this.endpoint('partner-stock/partner/all'),
        method: 'get',
        success: (data) => {
          this.partners = data || [];
        },
      });
    },
    fetchDuplicates() {
      this.loading = true;
      const params = { limit: 50, offset: 0 };
      if (this.search && this.search.trim()) params.search = this.search.trim();
      if (this.hasInventory) params.has_inventory = 'true';
      this.request({
        url: this.endpoint('product/adminProductDuplicates'),
        method: 'get',
        params,
        success: (data) => {
          this.groups = data.results || [];
          this.groups.forEach((g) => {
            this.$set(this.selectedMain, g.match_key, g.suggested_ref_id);
            this.$set(this.includedIds, g.match_key, new Set(
                g.products.filter((p) => p.id !== g.suggested_ref_id).map((p) => p.id)
            ));
          });
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    },
    onMainChange(group) {
      const mainId = this.selectedMain[group.match_key];
      this.$set(this.includedIds, group.match_key, new Set(
          group.products.filter((p) => p.id !== mainId).map((p) => p.id)
      ));
    },
    isIncluded(group, id) {
      const set = this.includedIds[group.match_key];
      return set ? set.has(id) : false;
    },
    toggleIncluded(group, id) {
      const set = this.includedIds[group.match_key];
      if (!set) return;
      if (set.has(id)) set.delete(id);
      else set.add(id);
      this.$set(this.includedIds, group.match_key, new Set(set));
    },
    includedCount(group) {
      const set = this.includedIds[group.match_key];
      return set ? set.size : 0;
    },
    statusLabel(status) {
      return this.statusLabelMap[status] || status;
    },
    fmtNum(n) {
      return (n || 0).toLocaleString('fa-IR');
    },
    storeInventoryTotal(report) {
      const list = report && report.changes && report.changes.store_inventory_moved;
      return list && list.length;
    },
    redirectEntries(report) {
      const redirected = report && report.changes && report.changes.redirected_references;
      return redirected ? Object.entries(redirected) : [];
    },
    redirectConflictEntries(report) {
      const dropped = report && report.changes && report.changes.redirect_conflicts_dropped;
      return dropped ? Object.entries(dropped) : [];
    },
    fieldLabel(category, field) {
      return (this.fieldLabels[category] && this.fieldLabels[category][field]) || field;
    },
    fieldRows(report) {
      if (!report || !report.changes) return [];
      const rows = [];
      const {
        content = {}, dimensions = {}, pricing = {}, picture,
      } = report.changes;

      Object.entries(content).forEach(([field, info]) => {
        rows.push({
          category: 'content',
          field,
          label: this.fieldLabel('content', field),
          sourceProduct: info.source_product,
          detail: `${this.fmtNum(info.from_len)} ← ${this.fmtNum(info.to_len)} کاراکتر`,
          applied: info.applied,
        });
      });
      Object.entries(dimensions).forEach(([field, info]) => {
        rows.push({
          category: 'dimensions',
          field,
          label: this.fieldLabel('dimensions', field),
          sourceProduct: info.source_product,
          detail: `${info.from == null ? '—' : info.from} ← ${info.to}`,
          applied: info.applied,
        });
      });
      Object.entries(pricing).forEach(([field, info]) => {
        rows.push({
          category: 'pricing',
          field,
          label: this.fieldLabel('pricing', field),
          sourceProduct: info.source_product,
          detail: `${info.from == null ? '—' : this.fmtNum(info.from)} ← ${this.fmtNum(info.to)}`,
          applied: info.applied,
        });
      });
      if (picture) {
        rows.push({
          category: 'picture',
          field: 'main',
          label: this.fieldLabel('picture', 'main'),
          sourceProduct: picture.source_product,
          detail: '',
          applied: picture.applied,
        });
      }
      return rows;
    },
    pricingOverrideRows(report) {
      const overrides = report && report.changes && report.changes.pricing_overrides;
      if (!overrides) return [];
      const fmt = (field, value) => {
        if (field === 'currency_id') return this.currencyName(value);
        if (field === 'profit_type' || field === 'discount_type') return this.profitTypeLabel(value);
        return this.fmtNum(value);
      };
      return Object.entries(overrides).map(([field, info]) => ({
        field,
        label: this.pricingLabelMap[field] || field,
        from: fmt(field, info.from),
        to: fmt(field, info.to),
      }));
    },
    profitTypeLabel(type) {
      return type === 'amount' ? 'مبلغی' : 'درصدی';
    },
    currencyName(id) {
      if (id == null) return '—';
      const c = this.currencies.find((cur) => cur.id === id);
      return c ? c.name : `#${id}`;
    },
    partnerName(id) {
      if (id == null) return 'بدون شریک';
      const p = this.partners.find((pt) => pt.id === id);
      return p ? p.name : `#${id}`;
    },
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? `${text.slice(0, n)}…` : text;
    },
    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },
    openQuickView(product) {
      this.quickView = { open: true, busy: true, data: null };
      this.request({
        url: this.endpoint(`product/adminProductGet/${product.id}`),
        method: 'get',
        success: (data) => {
          this.quickView.data = data;
          this.quickView.busy = false;
        },
        error: () => {
          this.quickView.busy = false;
          this.quickView.open = false;
        },
      });
    },
    isExcluded(category, field) {
      const set = this.excluded[category];
      return set ? set.has(field) : false;
    },
    toggleField(category, field) {
      const set = this.excluded[category];
      if (!set) return;
      if (set.has(field)) set.delete(field);
      else set.add(field);
      this.$set(this.excluded, category, new Set(set));
    },
    extractErrorMessage(err) {
      const data = err && err.response && err.response.data;
      if (!data) return 'خطا در برقراری ارتباط با سرور';
      if (data.error) return data.error;
      if (Array.isArray(data)) {
        return data.map((e) => {
          if (typeof e === 'string') return e;
          const msgs = e.messages || [];
          return Array.isArray(msgs) ? msgs.join('، ') : String(msgs);
        }).join(' | ');
      }
      return 'خطا در ادغام محصولات';
    },
    usePricingSource(product) {
      this.mainPricingSourceId = product.id;
      this.pricingSourceExplicit = true;
      this.mainPricing = {
        currency_id: product.currency,
        base_price: product.base_price,
        profit_margin: product.profit_margin,
        discount_margin: product.discount_margin,
      };
      this.mainShippingCost = product.shipping_cost;
      this.mainProfitType = product.profit_type;
      this.mainDiscountType = product.discount_type;
    },
    previewMerge(group) {
      const mainId = this.selectedMain[group.match_key];
      const duplicateIds = Array.from(this.includedIds[group.match_key] || []);
      if (!duplicateIds.length) return;

      const mainProduct = group.products.find((p) => p.id === mainId);
      this.mainStatus = mainProduct ? mainProduct.status : null;
      this.mainPartnerId = mainProduct && mainProduct.partner ? mainProduct.partner.id : null;
      this.mainPricing = mainProduct ? {
        currency_id: mainProduct.currency,
        base_price: mainProduct.base_price,
        profit_margin: mainProduct.profit_margin,
        discount_margin: mainProduct.discount_margin,
      } : {
        currency_id: null, base_price: null, profit_margin: null, discount_margin: null,
      };
      this.mainShippingCost = mainProduct ? mainProduct.shipping_cost : 0;
      this.mainProfitType = mainProduct ? mainProduct.profit_type : 'percent';
      this.mainDiscountType = mainProduct ? mainProduct.discount_type : 'percent';
      this.mainRegularPrice = mainProduct ? mainProduct.regular_price : null;
      this.mainPricingSourceId = mainId;
      this.pricingSourceExplicit = false;

      this.excluded = {
        content: new Set(),
        dimensions: new Set(),
        pricing: new Set(),
        gallery: new Set(),
        picture: new Set(),
        storeInventory: new Set(),
      };
      this.previewDialog = {
        open: true, group, report: null, busy: true, finalReport: null, finalError: null,
      };

      const requestOptions = {
        url: this.endpoint('product/adminProductMergePreview'),
        method: 'post',
        data: { main_id: mainId, duplicate_ids: duplicateIds },
        success: (data) => {
          this.previewDialog.report = data;
          this.previewDialog.busy = false;
        },
        error: (err) => {
          this.previewDialog.busy = false;
          this.previewDialog.open = false;
          this.handleError(err, requestOptions);
        },
      };
      this.request(requestOptions);
    },
    confirmApply() {
      if (!this.previewDialog.report) return;
      const { main_id: mainId, duplicate_ids: duplicateIds } = this.previewDialog.report;

      const requestOptions = {
        url: this.endpoint('product/adminProductMerge'),
        method: 'post',
        data: {
          main_id: mainId,
          duplicate_ids: duplicateIds,
          excluded_content_fields: Array.from(this.excluded.content),
          excluded_dimension_fields: Array.from(this.excluded.dimensions),
          excluded_pricing_fields: Array.from(this.excluded.pricing),
          excluded_gallery_ids: Array.from(this.excluded.gallery),
          excluded_store_inventory_keys: Array.from(this.excluded.storeInventory),
          exclude_picture: this.excluded.picture.has('main'),
          main_status: this.mainStatus,
          main_partner_id: this.mainPartnerId,
          main_currency_id: this.mainPricing.currency_id,
          main_base_price: this.mainPricing.base_price,
          main_profit_margin: this.mainPricing.profit_margin,
          main_discount_margin: this.mainPricing.discount_margin,
          ...(this.pricingSourceExplicit ? {
            main_shipping_cost: this.mainShippingCost,
            main_profit_type: this.mainProfitType,
            main_discount_type: this.mainDiscountType,
          } : {}),
        },
        success: (data) => {
          this.applyBusy = false;
          this.previewDialog.finalReport = data;
          this.previewDialog.finalError = null;
        },
        error: (err) => {
          this.applyBusy = false;
          this.previewDialog.finalReport = null;
          this.previewDialog.finalError = this.extractErrorMessage(err);
          this.handleError(err, requestOptions);
        },
      };
      this.applyBusy = true;
      this.request(requestOptions);
    },
    closeResultDialog() {
      const success = !!this.previewDialog.finalReport;
      const group = this.previewDialog.group;
      this.previewDialog.open = false;
      if (success) {
        this.notify('ادغام با موفقیت انجام شد', 'success');
        this.groups = this.groups.filter((g) => g !== group);
      }
    },
  },
};
</script>

<style scoped>
::v-deep .dark-v-dialog .v-card {
  background: rgba(2, 6, 23, 0.78) !important;
  border: 1px solid rgba(255, 255, 255, 0.10) !important;
  backdrop-filter: blur(18px) saturate(160%) !important;
  -webkit-backdrop-filter: blur(18px) saturate(160%) !important;
  color: rgba(226, 232, 240, 0.95) !important;
}

::v-deep .dark-v-dialog .v-card__title,
::v-deep .dark-v-dialog .v-card__text {
  color: rgba(226, 232, 240, 0.95) !important;
}

::v-deep .v-overlay__scrim {
  background: rgba(0, 0, 0, 0.65) !important;
}
</style>
